import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d70a5a1qa83q0hmd16hg";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("email", email);
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setEmail("");
      }
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#E8E3D8] pt-20 pb-0">
      <div className="w-full px-8 md:px-16">

        {/* Top Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pb-14 border-b border-[#0D0D0D]/10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-[#0D0D0D] mb-4 font-bold">
              Visit
            </p>
            <p className="font-body text-[#2B2B2B] text-sm leading-relaxed">
              Town House<br />
              Kingston University<br />
              53–57 Penrhyn Road<br />
              Kingston upon Thames<br />
              KT1 2EE, London
            </p>
            <p className="font-body text-[#E63946] text-sm mt-3 font-medium">26 March 2026</p>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-[#0D0D0D] mb-4 font-bold">
              Connect
            </p>
            <div className="space-y-2">
              {[
                { label: "Instagram", href: "#" },
                { label: "TikTok", href: "#" },
                { label: "Pinterest", href: "#" },
                { label: "Contact Us", href: "#" },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  rel="nofollow"
                  className="flex items-center gap-2 font-body text-sm text-[#2B2B2B] hover:text-[#E63946] transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-up-line text-xs" />
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-[#0D0D0D] mb-4 font-bold">
              Explore
            </p>
            <div className="space-y-2">
              {[
                { label: "About the Project", href: "#about" },
                { label: "Why Imperfection", href: "#imperfection" },
                { label: "Research & Insight", href: "#research" },
                { label: "Characters", href: "#characters" },
                { label: "KAPDAA", href: "#bag-charms" },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="flex items-center gap-2 font-body text-sm text-[#2B2B2B] hover:text-[#1D3557] transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-xs" />
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-[#0D0D0D] mb-4 font-bold">
              Stay Updated
            </p>
            <p className="font-body text-[#2B2B2B]/60 text-sm leading-relaxed mb-4">
              Get exhibition updates, behind-the-scenes content, and KAPDAA news.
            </p>
            {submitted ? (
              <div className="bg-[#1D3557] rounded-xl px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[2px] text-white">
                  You&apos;re on the list!
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-white border border-[#0D0D0D]/15 rounded-full px-4 py-2.5 font-body text-sm text-[#0D0D0D] placeholder:text-[#0D0D0D]/30 outline-none focus:border-[#1D3557] transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1D3557] text-white rounded-full py-2.5 font-mono text-[10px] uppercase tracking-[3px] hover:bg-[#E63946] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Subscribe"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Giant Wordmark Row */}
        <div className="py-8 flex items-center justify-between">
          <h2
            className="font-display text-[#0D0D0D]/15 leading-none select-none"
            style={{ fontSize: "clamp(48px, 8vw, 100px)" }}
          >
            OFF-PATTERN
          </h2>
          <div className="hidden md:flex items-center gap-4">
            <img
              src="https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/bf815b424deca47096daf3a9a4c9e38c.png"
              alt="OFF-PATTERN Logo"
              className="h-10 w-auto opacity-50 object-contain"
            />
          </div>
        </div>

        {/* Bottom Row */}
        <div className="py-6 border-t border-[#0D0D0D]/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[11px] text-[#0D0D0D]/40">
            &copy; 2026 OFF-PATTERN &times; KAPDAA. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {["Privacy", "Terms", "Accessibility"].map((item) => (
              <a
                key={item}
                href="#"
                rel="nofollow"
                className="font-mono text-[11px] text-[#0D0D0D]/40 hover:text-[#0D0D0D]/60 transition-colors cursor-pointer"
              >
                {item}
              </a>
            ))}
          </div>
          <p className="font-mono text-[11px] text-[#1D3557]/60 uppercase tracking-[2px]">
            Kingston University
          </p>
        </div>

      </div>
    </footer>
  );
}
